import axios from "axios";
import { API_BASE_URL } from "../config";
import { getAuthToken } from "./authService";
import { asErr } from "../utils/apiHelpers";

/** Params for public mosque incident (snake_case per Swagger) */
export interface CreateMosqueIncidentParams {
  type: string;
  indecent_type?: number;
  mosque_id: number;
  national_id?: string;
  email?: string;
  mobile_number?: string;
  country_name?: string;
  date_of_birth?: string;
  description?: string;
  platform?: string;
  counter?: number;
  report_date_time?: string;
  attachment?: string;
  attachment_filename?: string;
}

/** Request body: Swagger expects "params" (lowercase) */
interface CreateMosqueIncidentRequest {
  params?: CreateMosqueIncidentParams;
}

/** Odoo response: Result.Success and Result.Error */
interface OdooResult {
  Success?: boolean;
  Error?: string | null;
}

interface CreateMosqueIncidentResponse {
  Result?: OdooResult | null;
}

const getBaseURL = () => {
  if (process.env.NODE_ENV === "development") return "";
  return API_BASE_URL;
};

const http = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json", "Content-Type": "application/json" },
  timeout: 60000,
});

http.interceptors.request.use((config) => {
  const tok = getAuthToken();
  if (tok) (config.headers as any).Authorization = `Bearer ${tok}`;
  return config;
});

/**
 * POST /api/public/mosque/incident/create
 * Sends a mosque incident report. Public, no auth required.
 */
/**
 * POST /api/mosque/incident/create
 * Sends a mosque incident report. Requires JWT auth.
 */
export async function createMosqueIncident(
  params: CreateMosqueIncidentParams
): Promise<CreateMosqueIncidentResponse> {
  const body: CreateMosqueIncidentRequest = { params };

  try {
    const { data } = await http.post<CreateMosqueIncidentResponse>(
      "/api/mosque/incident/create",
      body
    );

    if (data?.Result?.Success === false) {
      const msg = data.Result.Error ?? "Unknown error from server.";
      throw new Error(msg);
    }

    return data ?? {};
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.data) {
      const d = e.response.data as Record<string, unknown>;
      let msg: string | null = null;

      if (d && typeof d === "object") {
        const detail = d.detail;
        const message = d.message;
        const error = d.error;
        const title = d.title;

        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail) && detail.length > 0) {
          const first = detail[0];
          msg =
            typeof first === "string"
              ? first
              : first && typeof first === "object" && "msg" in first
              ? String((first as any).msg)
              : JSON.stringify(first);
        } else if (typeof message === "string") msg = message;
        else if (typeof error === "string") msg = error;
        else if (typeof title === "string") msg = title;
        else if (Array.isArray(d.errors) && d.errors.length > 0) {
          const first = (d.errors as any[])[0];
          msg = typeof first === "string" ? first : String(first?.message ?? first);
        }
      }

      throw new Error(
        msg && msg.trim()
          ? msg
          : "طلب غير صالح (400). تحقق من البيانات أو تواصل مع الدعم."
      );
    }

    if (e instanceof Error) throw e;

    throw asErr("إرسال البلاغ فشل", e);
  }
}